// YERİ: src/app/domains/website/sections/mkt-service-catalog.section.ts
//
// Hizmet kataloğu — Melbarrel'ın YAZILIM DIŞI yarısı: marka, web, pazarlama,
// sosyal medya, bakım, e-ticaret.
//
// Neden ayrı bir bölüm tipi: `mkt-services` bir ÖZET (üç kart, slayt). Burada
// ziyaretçi "logo da yaptırabilir miyim, Google reklamı da veriyor musunuz"
// sorusunun cevabını EKSİKSİZ görmeli — o yüzden her kategorinin TÜM kalemleri
// listelenir. Katalog uzun olur; olması gereken de bu.
//
// SEO/AEO notu: kalemlerin hepsi HTML'de. Bir yapay zekâ "Toronto'da logo ve
// Google Ads'i aynı yerden veren var mı" diye sorulduğunda cevabı ancak bu
// listeyi okuyabiliyorsa verebilir.

import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from '../core/language.store';
import { WebCmsService } from '../core/web-cms.service';
import { WebDiscount, WebServiceCatalog } from '../core/web.models';
import { MktIcon } from './mkt-icon';
import { MktReveal } from './mkt-motion';
import { MktRouterLink, resolveMktLink } from './mkt-link';
import { PriceLabels, DEFAULT_PRICE_LABELS, fillTokens, formatItemPrice } from './mkt-price';

interface CatalogItem {
  /** Service.Code — kampanya eslestirmesi bunun uzerinden. CMS'te elle
      yazilmis kalemlerde YOK, o kalemler rozet almaz. */
  code?: string;
  name?: string;
  description?: string;
  price?: number | null;
  priceType?: string | null;
  includedInPackage?: boolean;
  isAddon?: boolean;
}

interface CatalogCategory {
  icon?: string;
  eyebrow?: string;
  title?: string;
  lead?: string;
  /** Bu kategorinin fiyat/teslim çerçevesi — "aylık", "tek seferlik" gibi. */
  note?: string;
  /**
   * Service.Category karşılığı. Dolu ve source='services' ise kalemler
   * veritabanından gelir.
   */
  key?: string;
  /**
   * Yalnız source !== 'services' iken kullanılır. Dinamik katalogda CMS
   * bloğunda `items` BULUNMAZ — 2026-08-08 migration'ı temizledi.
   */
  items?: CatalogItem[];
}

@Component({
  selector: 'mkt-service-catalog',
  standalone: true,
  imports: [RouterLink, MktIcon, MktReveal],
  host: { class: 'block bg-[color:var(--mkt-paper)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">

      <div class="mx-auto max-w-3xl text-center" mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow justify-center">{{ eyebrow() }}</p>
        }
        @if (lead()) {
          <h1 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h1>
        } @else {
          <h2 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
        }
        @if (subtitle()) {
          <p class="mkt-lead mt-5">{{ subtitle() }}</p>
        }
      </div>

      <div class="mt-20 space-y-20">
        @for (category of categories(); track $index; let i = $index) {
          <section [mktReveal]="i * 40" class="scroll-mt-28" [attr.id]="anchor(category)">

            <div class="grid gap-10 lg:grid-cols-[22rem_1fr] lg:gap-16">

              <!-- Kategori künyesi: yapışkan, liste uzun olduğu için ziyaretçi
                   kaydırırken hangi kategoride olduğunu kaybetmesin. -->
              <div class="lg:sticky lg:top-28 lg:self-start">
                <span class="grid size-12 place-items-center rounded-xl text-white"
                  style="background: var(--mkt-gradient)">
                  <mkt-icon [name]="category.icon || 'sparkles'" [size]="22" />
                </span>

                @if (category.eyebrow) {
                  <p class="mkt-eyebrow mt-5">{{ category.eyebrow }}</p>
                }
                <h3 class="mkt-h2 mt-2.5 text-2xl sm:text-[1.75rem]/[1.18]">{{ category.title }}</h3>
                @if (category.lead) {
                  <p class="mt-4 leading-relaxed text-[color:var(--mkt-text-muted)]">{{ category.lead }}</p>
                }
                @if (category.note) {
                  <p class="mt-5 rounded-xl border border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper-soft)] px-4 py-3 text-[0.8125rem] leading-relaxed text-[color:var(--mkt-text-faint)]">
                    {{ category.note }}
                  </p>
                }
              </div>

              <ul class="grid gap-3 sm:grid-cols-2">
                @for (service of category.items ?? []; track $index) {
                  <li class="mkt-card flex gap-3 p-4">
                    <mkt-icon name="check" [size]="15"
                      class="mt-1 shrink-0 text-[color:var(--mkt-accent)]" />
                    <div class="min-w-0 flex-1">
                      <div class="flex items-baseline justify-between gap-3">
                        <p class="text-[0.9375rem] font-bold leading-snug">{{ service.name }}</p>
                        <!-- Fiyat kalemin YANINDA: ziyaretçi "bunu da yapıyor
                             musunuz" sorusunun cevabıyla birlikte "kaça"
                             sorusunun cevabını da görmeli. Rakam Service
                             tablosundan gelir, elle yazılmaz. -->
                        @if (priceOf(service); as price) {
                          <span class="shrink-0 whitespace-nowrap text-[0.8125rem] font-bold tracking-tight text-[color:var(--mkt-accent)]">
                            {{ price }}
                          </span>
                        }
                      </div>
                      @if (service.description) {
                        <p class="mt-1 text-[0.8125rem] leading-relaxed text-[color:var(--mkt-text-faint)]">
                          {{ service.description }}
                        </p>
                      }

                      @if (kampanya(service); as k) {
                        <!-- Kampanya rozeti. Fiyatin KENDISI dusurulmuyor:
                             indirim odeme aninda fatura adresine gore
                             cozuluyor, burada hesaplanan bir sayi kasadakiyle
                             ayrisabilirdi. -->
                        <p class="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-lg bg-[color:var(--mkt-accent-soft)] px-2.5 py-1.5 text-[0.6875rem] font-bold text-[color:var(--mkt-accent)]">
                          <mkt-icon name="check" [size]="12" class="shrink-0" />
                          {{ k.label }}
                          @if (bundleAdlari(k); as adlar) {
                            <span class="font-medium opacity-80">({{ adlar }})</span>
                          }
                        </p>
                      }
                    </div>
                  </li>
                }
              </ul>
            </div>
          </section>
        }
      </div>

      @if (ctaText()) {
        <div class="mt-24 rounded-2xl p-px" style="background: var(--mkt-gradient)" mktReveal>
          <div class="rounded-[calc(1rem-1px)] bg-[color:var(--mkt-surface)] px-8 py-12 text-center sm:px-14">
            <h3 class="mkt-h2 text-2xl sm:text-[1.875rem]/[1.18]">{{ ctaTitle() }}</h3>
            @if (ctaSubtitle()) {
              <p class="mkt-lead mx-auto mt-4 max-w-2xl">{{ ctaSubtitle() }}</p>
            }
            <a [routerLink]="link(ctaUrl())" class="mkt-btn mkt-btn-primary mt-8">
              {{ ctaText() }}
            </a>
          </div>
        </div>
      }
    </div>
  `,
})
export class MktServiceCatalogSection {
  private langStore = inject(LanguageStore);
  private cms = inject(WebCmsService);

  readonly config = input.required<SiteConfig>();
  /** Sayfanin ilk bolumuyse baslik h1 basar (bkz. MktHost.lead). */
  readonly lead = input<boolean>(false);

  protected eyebrow     = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title       = computed(() => cfg(this.config(), 'title', ''));
  /** `{total}` / `{modules}` / `{currency}` yer tutucuları veriden doldurulur. */
  protected subtitle    = computed(() => {
    const data = this.catalog();
    return fillTokens(cfg(this.config(), 'subtitle', ''), {
      total: data?.totalCount ?? '',
      modules: data?.moduleCount ?? '',
      currency: data?.currency ?? '',
    });
  });
  protected ctaTitle    = computed(() => cfg(this.config(), 'ctaTitle', ''));
  protected ctaSubtitle = computed(() => cfg(this.config(), 'ctaSubtitle', ''));
  protected ctaText     = computed(() => cfg(this.config(), 'ctaText', ''));
  protected ctaUrl      = computed(() => cfg(this.config(), 'ctaUrl', 'contact'));

  /**
   * Kalem kaynağı: 'services' → veritabanı, başka her şey → CMS'teki liste.
   *
   * Varsayılan 'services'. Katalog elle yazıldığı dönemde 2026-08-07 fiyat
   * revizyonunda pasifleştirilen 32 hizmeti satılıyormuş gibi listelemeye devam
   * etti ve yeni AI asistanlarını hiç göstermedi. Tek gerçek DB olsun.
   */
  protected source = computed(() => cfg<string>(this.config(), 'source', 'services'));

  private catalog = signal<WebServiceCatalog | null>(null);

  // ── Kampanya rozetleri ─────────────────────────────────────────────────
  // Ayri istek: kampanya cekilemezse katalog yine gorunmeli. Cografya
  // gonderilmiyor — ziyaretcinin fatura adresi bilinmiyor, dolayisiyla yalniz
  // her yerde gecerli kampanyalar doner.
  private discounts = signal<WebDiscount[]>([]);

  /**
   * Bu kaleme dusen kampanya. Hem hizmete OZEL indirimi hem de kalemin
   * girdigi BUNDLE'i kapsar; birden fazlaysa en yuksek deger secilir —
   * motor da odeme aninda musteri lehine olani seciyor, vitrin baska bir sey
   * vaat etmemeli.
   *
   * Genel (scope='all') kampanyalar BURADA gosterilmiyor: her kalemin altina
   * ayni rozeti basmak sayfayi okunmaz hale getirirdi; onlarin yeri fiyat
   * sayfasindaki serit.
   */
  protected kampanya(item: CatalogItem): WebDiscount | null {
    const kod = (item.code ?? '').trim().toUpperCase();
    if (!kod) return null;

    const candidates = this.discounts().filter((d) =>
      (d.scope === 'service' && (d.targetServiceCode ?? '').toUpperCase() === kod)
      || (d.scope === 'bundle'
          && (d.bundleServiceCodes ?? []).some((c) => (c ?? '').toUpperCase() === kod)));

    if (!candidates.length) return null;
    return candidates.reduce((a, b) => (b.value > a.value ? b : a));
  }

  /**
   * Bundle'daki hizmet adlari — KATALOGDAN, uctan gelen adlardan degil.
   *
   * Uc ham `Service.Name` doner (Ingilizce). Ayni sayfada kartlar cevrilmis
   * adi ("Logo tasarimi"), rozet cevrilmemis adi ("Logo Design") basiyordu;
   * ziyaretci ayni hizmeti iki farkli isimle goruyordu. Katalog kalemleri
   * ziyaretcinin dilinde geldigi icin ad kaynagi TEK olsun.
   *
   * Katalogda bulunmayan kod (pasiflestirilmis hizmet) uctan gelen adla
   * yedeklenir — rozetten hizmet dusurmek, adi Ingilizce basmaktan kotu.
   */
  protected bundleAdlari(k: WebDiscount): string {
    if (k.scope !== 'bundle') return '';

    const kodlar = k.bundleServiceCodes ?? [];
    if (!kodlar.length) return (k.bundleServiceNames ?? []).join(' + ');

    const katalogAdi = new Map<string, string>();
    for (const kategori of this.catalog()?.categories ?? []) {
      for (const kalem of kategori.items ?? []) {
        if (kalem.code) katalogAdi.set(kalem.code.toUpperCase(), kalem.name);
      }
    }

    const yedek = k.bundleServiceNames ?? [];
    return kodlar
      .map((kod, i) => katalogAdi.get((kod ?? '').toUpperCase()) ?? yedek[i] ?? '')
      .filter((ad) => ad.length > 0)
      .join(' + ');
  }

  constructor() {
    effect(() => {
      if (this.source() !== 'services') return;

      // Dil değişince katalog da o dilde yeniden çekilir.
      const lang = this.langStore.current();

      this.cms.getPublicDiscounts().subscribe({
        next: (list) => this.discounts.set(list),
        error: () => this.discounts.set([]),
      });

      this.cms.getServiceCatalog(lang).subscribe({
        next: (data) => this.catalog.set(data),
        // Uç düşerse katalog BOŞ çizilir — gerekçe categories() içinde.
        error: () => this.catalog.set(null),
      });
    });
  }

  /**
   * CMS kategori ÇERÇEVESİ (başlık, spot, ikon, not) + DB'den gelen KALEMLER.
   * Çerçeve metni sayfayla birlikte çevriliyor; kalemler ServiceTranslation'dan.
   */
  protected categories = computed<CatalogCategory[]>(() => {
    const raw = this.config()['categories'];
    const frames: CatalogCategory[] = Array.isArray(raw) ? raw : [];

    if (this.source() !== 'services') return frames;

    // Katalog gelmediyse (uç düştü, istek daha bitmedi) HİÇBİR kalem çizilmez.
    // Bir dönem CMS'teki listeye düşülüyordu; 2026-08-07 fiyat revizyonundan
    // sonra bu, uç her düştüğünde pasifleştirilmiş 32 hizmeti satılıyormuş gibi
    // reklam etmek anlamına geliyordu. Eksik katalog, yanlış kataloğdan iyidir.
    const data = this.catalog();
    if (!data) return [];

    const byKey = new Map(data.categories.map((c) => [c.key, c.items]));

    // Kategori DB'de boşsa (hepsi pasifleştirilmişse) çerçeve de çizilmez.
    const framed = frames
      .map((frame) => ({ ...frame, items: (frame.key ? byKey.get(frame.key) : undefined) ?? [] }))
      .filter((frame) => frame.items.length > 0);

    // ÇERÇEVESİ OLMAYAN kategoriler. Eskiden sessizce düşüyordu: panelde
    // "vitrinde göster" işaretli bir hizmet, kategorisi için CMS'te kart yoksa
    // sitede hiç görünmüyor, ama "{total} hizmet" sayısına yine giriyordu —
    // platform ile site iki ayrı liste gösteriyordu. Artık sonda tek bir
    // "Diğer hizmetler" grubunda listelenir (başlık CMS'ten).
    const framedKeys = new Set(frames.map((f) => f.key).filter(Boolean));
    const orphans = data.categories
      .filter((c) => !framedKeys.has(c.key))
      .flatMap((c) => c.items);

    if (orphans.length === 0) return framed;

    return [...framed, {
      icon: 'sparkles',
      title: cfg(this.config(), 'otherTitle', 'More services'),
      lead: cfg(this.config(), 'otherLead', ''),
      items: orphans,
    }];
  });

  /** Birim sözcükleri CMS'te (çevrilir), tutar veride (çevrilmez). */
  private labels = computed<PriceLabels>(() => ({
    from: cfg(this.config(), 'priceLabel', DEFAULT_PRICE_LABELS.from),
    month: cfg(this.config(), 'unitMonth', DEFAULT_PRICE_LABELS.month),
    hour: cfg(this.config(), 'unitHour', DEFAULT_PRICE_LABELS.hour),
    page: cfg(this.config(), 'unitPage', DEFAULT_PRICE_LABELS.page),
    clip: cfg(this.config(), 'unitClip', DEFAULT_PRICE_LABELS.clip),
    language: cfg(this.config(), 'unitLanguage', DEFAULT_PRICE_LABELS.language),
    included: cfg(this.config(), 'includedText', DEFAULT_PRICE_LABELS.included),
  }));

  /** Boş dönerse şablon fiyat rozetini hiç çizmez (elle yazılmış kalemler). */
  protected priceOf(item: CatalogItem): string {
    if (item.price === undefined && !item.includedInPackage) return '';
    return formatItemPrice(
      { price: item.price, priceType: item.priceType, includedInPackage: !!item.includedInPackage },
      this.catalog()?.currency ?? '',
      this.langStore.current(),
      this.labels(),
    );
  }

  /** Menüden/footer'dan doğrudan bir kategoriye link verilebilsin (#branding). */
  protected anchor(category: CatalogCategory): string {
    return (category.title ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}
